import React from 'react';
import { View, Pressable } from 'react-native';
import { useHospitalityStore } from '../store/useHospitalityStore';
import { useTheme } from '../theme';
import {
  Txt, Card, CardHead, CardTitle, CardSub, CardBody,
  Chip, Avatar, Icon, Input, Field, Button, buttonFg,
  Screen, Row, Col, Spread, Cluster, GuestPicker,
} from '../components';
import { findOrCreateGuest } from '../services/crm/guest';
import { refreshReservations } from '../services/hospitalitySync';
import {
  createReservation, updateReservation, cancelReservation, getAvailability, getTableAvailability,
} from '../services/reservations/reservation';
import type { SlotAvailability, TableOption } from '../schemas/reservations/reservation';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';
import { displayTime } from '../data/format';

/**
 * Reservations — the times grid is backed by SK Calendar: slots come from the
 * venue's operating hours (Settings → Venue), and Open/Filling/Full per slot is a
 * real cal_check_slots probe via the adapter (GET /reservations/availability).
 */

// Build tonight's bookable slot ISO list from the venue's HH:MM open/close hours,
// every 30 min, up to (but not past) close. Empty until hours are loaded.
function buildSlots(open: string, close: string): string[] {
  const toMin = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN;
  };
  const start = toMin(open);
  let end = toMin(close);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
  if (end <= start) end += 24 * 60; // close after midnight
  const out: string[] = [];
  for (let mins = start; mins <= end; mins += 30) {
    const d = new Date();
    d.setHours(Math.floor(mins / 60) % 24, mins % 60, 0, 0);
    out.push(d.toISOString());
  }
  return out;
}

export default function Reservations() {
  const t = useTheme();
  const s = useHospitalityStore();
  const { draft, resForm: f, resSlot, reservations, sections, tables, location } = s;

  // Slots derive from the venue's operating hours (real, per-venue).
  const slots = React.useMemo(() => buildSlots(location.open, location.close), [location.open, location.close]);

  // Real availability per slot, fetched from SK Calendar for the chosen section/party.
  const [avail, setAvail] = React.useState<Record<string, SlotAvailability['status']>>({});
  const [availLive, setAvailLive] = React.useState(false);

  // Re-fetch live availability for the current section/party. Called on
  // section/party/hours change AND after every booking/cancel so the grid
  // reflects consumed capacity instantly — no manual page reload. `signal`
  // (an optional {cancelled} box) lets the effect drop a stale in-flight result
  // when the inputs change again before it resolves.
  const refreshAvailability = React.useCallback(async (signal?: { cancelled: boolean }) => {
    if (!f.section || slots.length === 0) { setAvail({}); setAvailLive(false); return; }
    try {
      const rows = await getAvailability(f.section, f.size, slots);
      if (signal?.cancelled) return;
      const map: Record<string, SlotAvailability['status']> = {};
      for (const r of rows) map[r.time] = r.status;
      setAvail(map);
      setAvailLive(true);
    } catch {
      if (!signal?.cancelled) { setAvail({}); setAvailLive(false); }
    }
  }, [f.section, f.size, slots]);

  React.useEffect(() => {
    const signal = { cancelled: false };
    void refreshAvailability(signal);
    return () => { signal.cancelled = true; };
  }, [refreshAvailability]);

  // Per-table free/occupied at the chosen slot — powers the table picker. Null
  // selection = "Auto" (system assigns the smallest free fitting table).
  const [tableOptions, setTableOptions] = React.useState<TableOption[]>([]);
  const [selectedTableId, setSelectedTableId] = React.useState<string | null>(null);
  const refreshTableOptions = React.useCallback(async (signal?: { cancelled: boolean }) => {
    if (!resSlot || !f.section) { setTableOptions([]); return; }
    try {
      const rows = await getTableAvailability(f.section, f.size, resSlot);
      if (!signal?.cancelled) setTableOptions(rows);
    } catch {
      if (!signal?.cancelled) setTableOptions([]);
    }
  }, [resSlot, f.section, f.size]);
  React.useEffect(() => {
    const signal = { cancelled: false };
    setSelectedTableId(null); // reset the pick whenever slot/section/party changes
    // Debounce: clicking through times rapidly must not fan out a per-table
    // Calendar probe for every intermediate slot (that flooded the backbone
    // rate limit and made every table read as "Booked"). Only the slot you
    // settle on (350ms idle) triggers the fetch.
    const timer = setTimeout(() => { void refreshTableOptions(signal); }, 350);
    return () => { signal.cancelled = true; clearTimeout(timer); };
  }, [refreshTableOptions]);

  const bookSummary = resSlot ? `${f.size} guests · ${f.section} · ${displayTime(resSlot)}` : 'Pick an available time';

  // Real collision check for the VOICE draft — was a hardcoded "No collision ·
  // G4 open" chip regardless of what the draft actually said. Uses the same
  // live per-table availability the manual booking form already calls.
  // draft.time is already ISO (VOICE passes hosp_reserve_table's own `time`
  // arg straight through), same as everything else on this screen.
  const [draftAvail, setDraftAvail] = React.useState<TableOption[] | null>(null);
  const refreshDraftAvailability = React.useCallback(async (signal?: { cancelled: boolean }) => {
    if (!draft) { setDraftAvail(null); return; }
    try {
      const rows = await getTableAvailability(draft.section, draft.size, draft.time);
      if (!signal?.cancelled) setDraftAvail(rows);
    } catch {
      if (!signal?.cancelled) setDraftAvail([]);
    }
  }, [draft]);

  React.useEffect(() => {
    const signal = { cancelled: false };
    setDraftAvail(null);
    void refreshDraftAvailability(signal);
    return () => { signal.cancelled = true; };
  }, [refreshDraftAvailability]);

  // Real collision chip content — derived from draftAvail above, never a
  // hardcoded table name.
  const draftCollision: { variant: 'success' | 'warn' | 'default'; label: string } = (() => {
    if (draftAvail === null) return { variant: 'default', label: 'Checking availability…' };
    const free = draftAvail.find((tb) => tb.status === 'free' && tb.fits);
    if (free) return { variant: 'success', label: `No collision · ${free.label} open` };
    if (draftAvail.some((tb) => tb.status === 'unknown')) return { variant: 'default', label: 'Availability unknown' };
    return { variant: 'warn', label: `${draft?.section ?? 'Section'} fully booked` };
  })();

  // The optimistic row id is the newest reservation after the store action runs.
  const newestResId = () => useHospitalityStore.getState().reservations[0]?.id;

  // A reservation is a Calendar event against a real table (resource). The form
  // only picks a section, so resolve the best bookable table in that section:
  // prefer the smallest table that fits the party; fall back to any table there.
  // Returns the adapter table id (apiId) or undefined (adapter then 400s with a
  // clear "no table" message rather than skcal's opaque pool error).
  const pickTableId = (section: string, size: number): string | undefined => {
    const inSection = tables.filter((tbl) => tbl.section === section && tbl.apiId);
    if (inSection.length === 0) return undefined;
    const fits = inSection.filter((tbl) => tbl.seats >= size).sort((a, b) => a.seats - b.seats);
    return (fits[0] ?? inSection[0]).apiId;
  };

  // Acknowledge the VOICE-drafted booking. VOICE already created the real
  // reservation itself (hosp_reserve_table is the collision authority — it
  // doesn't wait for operator approval), so this must NOT call
  // createReservation again — that would double-book the table. This just
  // clears the card and pulls the fresh row into "Tonight" immediately
  // instead of waiting for the next unrelated refetch.
  const handleConfirmDraft = () => {
    s.declineDraft(); // clears draft locally (same as Dismiss)
    void refreshReservations();
  };

  // New-booking form → create the reservation in SK Calendar.
  const handleBook = async () => {
    if (!resSlot) { s.confirmBooking(); return; } // store shows the "pick a time" warning
    const slot = resSlot;
    // Resolve the guest to a CRM contact (deduped by phone) BEFORE the optimistic
    // update — a picked contact already has an id; a typed-in guest with a phone is
    // find-or-created. No phone → walk-in, no contact link.
    let contactId = f.contactId;
    if (!contactId && f.phone.trim()) {
      try {
        const g = await findOrCreateGuest({ name: f.name.trim() || 'Guest', phone: f.phone.trim() });
        contactId = g.id;
      } catch { /* CRM unreachable — proceed without a contact link */ }
    }
    s.confirmBooking(); // keep the optimistic local update
    const tempId = newestResId();
    try {
      const res = await createReservation({
        // Operator's explicit table pick wins; otherwise auto (smallest fitting).
        tableId: selectedTableId ?? pickTableId(f.section, f.size),
        tablePref: f.section,
        partySize: f.size,
        time: slot, // already ISO (slots come from buildSlots)
        // No durationMinutes → adapter derives the hold from the venue's turn_times.
        contactId,
        source: 'Operator',
        guest: { name: f.name || 'Walk-in guest', phone: f.phone || '+91 00000 00000' },
      });
      if (res?.id && tempId) s.replaceReservationId(tempId, res.id);
      showSuccess(res, 'Reservation booked');
      void refreshAvailability(); // reflect the just-consumed table immediately
      void refreshReservations(); // pull the row back with its allocated table label
      void refreshTableOptions(); // flip the just-taken table to Booked in the picker
    } catch (err) {
      handleApiError(err, 'Booking failed');
    }
  };

  // Confirm an existing pending reservation → update it in SK Calendar.
  const handleConfirmReservation = async (id: string) => {
    s.confirmReservation(id); // keep the optimistic local update
    try {
      const res = await updateReservation(id, { confirm: true });
      showSuccess(res, 'Reservation confirmed');
    } catch (err) {
      handleApiError(err, 'Confirm failed');
    }
  };

  // Cancel an existing reservation → delete it in SK Calendar.
  const handleCancelReservation = async (id: string) => {
    s.cancelReservation(id); // keep the optimistic local update
    try {
      const res = await cancelReservation(id);
      showSuccess(res, 'Reservation cancelled');
      void refreshAvailability(); // freed table → re-open the slot immediately
      void refreshReservations(); // drop the cancelled row from the list
      void refreshTableOptions(); // freed table → back to Free in the picker
    } catch (err) {
      handleApiError(err, 'Cancel failed');
    }
  };

  return (
    <Screen>
      <Row gap={16} style={{ alignItems: 'flex-start' }}>
        <Col gap={16} style={{ flex: 1.3 }}>
          {/* VOICE draft card */}
          {draft && (
            <Card style={{ borderColor: t.colors.accentLine }}>
              <CardHead style={{ backgroundColor: t.colors.accentSoft, borderBottomColor: t.colors.accentLine }}>
                <Row gap={10}>
                  <View style={{ width: 30, height: 30, borderRadius: 8, backgroundColor: t.colors.accent, alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="phone" size={16} color="#fff" strokeWidth={1.7} />
                  </View>
                  <Col gap={2}>
                    <Txt color="accentInk" weight="semibold">VOICE is drafting a booking</Txt>
                    <CardSub>Live call · {draft.phone}</CardSub>
                  </Col>
                </Row>
                <Row gap={6}>
                  <View style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: t.colors.accent }} />
                  <Txt color="accent" size="sm" weight="semibold">on call</Txt>
                </Row>
              </CardHead>
              <CardBody style={{ gap: 12 }}>
                <Row gap={14} style={{ alignItems: 'flex-start' }}>
                  <View style={{ flex: 1.4 }}>
                    <Txt color="ink4" size="xs" upper>Guest</Txt>
                    <Txt weight="semibold">{draft.name}</Txt>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Txt color="ink4" size="xs" upper>Party</Txt>
                    <Txt weight="semibold" mono>{draft.size}</Txt>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Txt color="ink4" size="xs" upper>Time · {draft.section}</Txt>
                    <Txt weight="semibold" mono>{displayTime(draft.time)}</Txt>
                  </View>
                </Row>
                <View style={{ backgroundColor: t.colors.paper2, borderRadius: 8, paddingVertical: 11, paddingHorizontal: 12 }}>
                  <Txt size="sm" color="ink2" style={{ lineHeight: 20 }}>{draft.summary}</Txt>
                </View>
                <Row gap={8}>
                  <Button variant="accent" onPress={handleConfirmDraft}>
                    <Icon name="check" size={15} color={buttonFg('accent', t.colors)} strokeWidth={1.8} />
                    <Txt size="md" weight="medium" color={buttonFg('accent', t.colors)}>Confirm booking</Txt>
                  </Button>
                  <Button variant="ghost" onPress={() => s.declineDraft()}>Dismiss</Button>
                  <Chip variant={draftCollision.variant} dot style={{ marginLeft: 'auto' }}>{draftCollision.label}</Chip>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* New booking form */}
          <Card>
            <CardHead>
              <Col gap={2}>
                <CardTitle>New booking</CardTitle>
                <CardSub>
                  {availLive
                    ? 'Availability is live from SK Calendar — full slots can’t be selected'
                    : 'Pick a section to check live availability'}
                </CardSub>
              </Col>
            </CardHead>
            <CardBody style={{ gap: 12 }}>
              <Row gap={14} style={{ alignItems: 'flex-start' }}>
                <Field label="Party size" style={{ flex: 1 }}>
                  <Row gap={8}>
                    <Button icon variant="default" onPress={() => s.setResForm('size', Math.max(1, f.size - 1))}>
                      <Icon name="minus" size={15} color={buttonFg('default', t.colors)} strokeWidth={2} />
                    </Button>
                    <Txt mono weight="semibold" size="xl" center style={{ flex: 1 }}>{f.size}</Txt>
                    <Button icon variant="default" onPress={() => s.setResForm('size', Math.min(12, f.size + 1))}>
                      <Icon name="plus" size={15} color={buttonFg('default', t.colors)} strokeWidth={2} />
                    </Button>
                  </Row>
                </Field>
                <Field label="Section" style={{ flex: 1.2 }}>
                  <Row gap={6} wrap>
                    {sections.length === 0 && (
                      <Txt size="sm" color="ink3">No sections yet — add them in Settings.</Txt>
                    )}
                    {sections.map((secObj) => {
                      const sec = secObj.name;
                      const active = f.section === sec;
                      return (
                        <Pressable
                          key={secObj.id}
                          onPress={() => s.setResForm('section', sec)}
                          style={{
                            flex: 1,
                            paddingVertical: 7,
                            paddingHorizontal: 6,
                            borderRadius: 8,
                            borderWidth: 1,
                            borderColor: active ? t.colors.accent : t.colors.line,
                            backgroundColor: active ? t.colors.accentSoft : t.colors.card,
                          }}
                        >
                          <Txt size="sm" weight="medium" center color={active ? 'accentInk' : 'ink2'}>{sec}</Txt>
                        </Pressable>
                      );
                    })}
                  </Row>
                </Field>
              </Row>
              <GuestPicker
                name={f.name}
                phone={f.phone}
                contactId={f.contactId}
                onChange={(v) => s.setResGuest(v)}
              />
              <View>
                <Txt size="sm" color="ink2" weight="medium">Available times · tonight</Txt>
                {slots.length === 0 && (
                  <Txt size="sm" color="ink3" style={{ marginTop: 6 }}>Set the venue’s operating hours in Settings to show bookable times.</Txt>
                )}
                <Cluster gap={8} style={{ marginTop: 7 }}>
                  {slots.map((time) => {
                    // A slot in the past can't be booked — SK Calendar rejects back-dating.
                    const past = new Date(time).getTime() < Date.now();
                    // Real status from SK Calendar. Until the probe resolves (or if it
                    // errored) the slot is 'unknown' — neutral, still selectable (the
                    // booking POST does the authoritative collision check). Never shown
                    // as a false 'Full'.
                    const status: SlotAvailability['status'] = past ? 'full' : (avail[time] ?? 'unknown');
                    const isSel = resSlot === time;
                    const disabled = past || status === 'full';

                    let borderColor = t.colors.line;
                    let backgroundColor = t.colors.card;
                    let fg: keyof typeof t.colors = 'ink2';
                    let opacity = 1;
                    if (isSel) {
                      borderColor = t.colors.accent; backgroundColor = t.colors.accentSoft; fg = 'accentInk';
                    } else if (past) {
                      fg = 'ink4'; opacity = 0.45; // muted, clearly not bookable
                    } else if (status === 'full') {
                      borderColor = t.colors.dangerLine; backgroundColor = t.colors.dangerSoft; fg = 'danger'; opacity = 0.7;
                    } else if (status === 'tight') {
                      borderColor = t.colors.warnLine; backgroundColor = t.colors.warnSoft; fg = 'warn';
                    } else if (status === 'unknown') {
                      fg = 'ink3'; opacity = 0.7; // loading / Calendar unreachable
                    }

                    return (
                      <Pressable
                        key={time}
                        disabled={disabled}
                        onPress={() => s.pickSlot(time, status)}
                        style={{
                          flexGrow: 1,
                          flexBasis: '18%',
                          alignItems: 'center',
                          gap: 1,
                          paddingVertical: 8,
                          paddingHorizontal: 4,
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor,
                          backgroundColor,
                          opacity,
                        }}
                      >
                        <Txt mono size="sm" weight="semibold" color={fg}>{displayTime(time).replace(' PM', '').replace(' AM', 'a')}</Txt>
                        <Txt mono size="xs" color={fg} style={{ opacity: 0.8 }}>
                          {past ? 'Past' : status === 'full' ? 'Full' : status === 'tight' ? 'Filling' : status === 'unknown' ? '·' : 'Open'}
                        </Txt>
                      </Pressable>
                    );
                  })}
                </Cluster>
              </View>

              {/* Table picker — appears once a time is chosen. Live free/occupied
                  from SK Calendar; occupied tables can't be selected. */}
              {resSlot && tableOptions.length > 0 && (
                <View>
                  <Txt size="sm" color="ink2" weight="medium">Table · {displayTime(resSlot)}</Txt>
                  <Cluster gap={8} style={{ marginTop: 7 }}>
                    {/* Auto option */}
                    <Pressable
                      onPress={() => setSelectedTableId(null)}
                      style={{
                        paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1,
                        borderColor: selectedTableId === null ? t.colors.accent : t.colors.line,
                        backgroundColor: selectedTableId === null ? t.colors.accentSoft : t.colors.card,
                      }}
                    >
                      <Txt size="sm" weight="semibold" color={selectedTableId === null ? 'accentInk' : 'ink2'}>Auto</Txt>
                      <Txt size="xs" color="ink3">smallest free fit</Txt>
                    </Pressable>
                    {tableOptions.map((tbl) => {
                      const isSel = selectedTableId === tbl.tableId;
                      const disabled = tbl.status === 'occupied'; // only truly-taken tables are unselectable
                      let borderColor = t.colors.line, backgroundColor = t.colors.card, fg: keyof typeof t.colors = 'ink2', opacity = 1;
                      if (isSel) { borderColor = t.colors.accent; backgroundColor = t.colors.accentSoft; fg = 'accentInk'; }
                      else if (tbl.status === 'occupied') { borderColor = t.colors.dangerLine; backgroundColor = t.colors.dangerSoft; fg = 'danger'; opacity = 0.7; }
                      else if (tbl.status === 'unknown') { fg = 'ink3'; opacity = 0.7; } // check couldn't complete — neutral, still selectable
                      else if (!tbl.fits) { fg = 'ink4'; opacity = 0.7; } // free but too small for the party
                      const statusLabel = tbl.status === 'occupied' ? 'Booked' : tbl.status === 'unknown' ? '·' : 'Free';
                      return (
                        <Pressable
                          key={tbl.tableId}
                          disabled={disabled}
                          onPress={() => setSelectedTableId(tbl.tableId)}
                          style={{
                            paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1,
                            borderColor, backgroundColor, opacity, alignItems: 'center', gap: 1,
                          }}
                        >
                          <Txt mono size="sm" weight="semibold" color={fg}>{tbl.label}</Txt>
                          <Txt mono size="xs" color={fg} style={{ opacity: 0.85 }}>
                            {tbl.seats} seats · {statusLabel}
                          </Txt>
                        </Pressable>
                      );
                    })}
                  </Cluster>
                </View>
              )}

              <Spread style={{ paddingTop: 4 }}>
                <CardSub>{bookSummary}</CardSub>
                <Button variant="accent" onPress={handleBook}>Confirm booking</Button>
              </Spread>
            </CardBody>
          </Card>
        </Col>

        {/* Tonight list */}
        <Col gap={16} style={{ flex: 1 }}>
          <Card>
            <CardHead>
              <Row gap={8}>
                <CardTitle>Tonight</CardTitle>
                <Chip>{String(reservations.length)}</Chip>
              </Row>
              <CardSub>SK Calendar</CardSub>
            </CardHead>
            <CardBody style={{ gap: 12 }}>
              {reservations.map((r) => (
                <View key={r.id} style={{ borderWidth: 1, borderColor: t.colors.line, borderRadius: 10, padding: 12 }}>
                  <Spread>
                    <Row gap={10}>
                      <Avatar initials={r.name.slice(0, 2)} size="sm" />
                      <Col gap={2}>
                        <Txt weight="medium" size="sm">{r.name} · {r.size}</Txt>
                        <Txt size="xs" color="ink3">{r.section}{r.table ? ` · ${r.table}` : ''} · {r.via}</Txt>
                      </Col>
                    </Row>
                    <Chip variant={r.status === 'confirmed' ? 'success' : 'warn'} dot>
                      {r.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                    </Chip>
                  </Spread>
                  <Spread style={{ marginTop: 10 }}>
                    <Txt mono size="lg" weight="semibold">{displayTime(r.time)}</Txt>
                    <Row gap={6}>
                      {r.status === 'pending' && (
                        <Button variant="accent" size="sm" onPress={() => handleConfirmReservation(r.id)}>Confirm</Button>
                      )}
                      <Button variant="ghost" size="sm" onPress={() => handleCancelReservation(r.id)}>Cancel</Button>
                    </Row>
                  </Spread>
                </View>
              ))}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Screen>
  );
}
